# Observability

Understand what `resolve()` is doing, how decisions are reached, and what's being produced.

---

## Enabling Debug Output

Add `debug: true` to your Taias instance to see what every `resolve()` call is doing:

```ts
const taias = createTaias({
  flow: musicDiscoveryFlow,
  debug: true,
});
```

Every `resolve()` call prints a formatted breakdown to the console:

```
┌─ Taias Resolve ─────────────────────────────
│ Flow: music_discovery_flow
│ Context: toolName=get_recent_tracks
│
│ Matched: step 0 (logic)
│ Phase: indexed
│ Resolution path: toolName
│ Candidates evaluated: 1
│ Match condition: {"toolName":{"is":"get_recent_tracks"}}
│
│ Decision: nextTool=get_recommendations
│ Advice: FOR THE BEST USER EXPERIENCE, TELL THE...
│ Duration: 0.12ms
└──────────────────────────────────────────────
```

When no step matches:

```
┌─ Taias Resolve ─────────────────────────────
│ Flow: music_discovery_flow
│ Context: toolName=unknown_tool
│
│ Result: NO MATCH
│ Candidates evaluated: 0
│
│ Decision: null
│ Duration: 0.04ms
└──────────────────────────────────────────────
```

No extra imports or wiring needed -- just the one option.

---

## Compact Format

For concise single-line output:

```ts
const taias = createTaias({
  flow: musicDiscoveryFlow,
  debug: { format: "compact" },
});
```

Produces:

```
[Taias] get_recent_tracks → nextTool=get_recommendations (indexed, step 0, 1 evaluated)
[Taias] unknown_tool → no match (0 evaluated)
```

---

## Custom Logger

By default, debug output goes to `console.log`. Route it to any logging framework:

```ts
const taias = createTaias({
  flow: musicDiscoveryFlow,
  debug: { logger: myLogger.debug.bind(myLogger) },
});
```

---

## Detailed Tracing

For debugging why a specific step did or didn't match, enable detailed tracing:

```ts
const taias = createTaias({
  flow: musicDiscoveryFlow,
  debug: true,
  tracing: "detailed",
});
```

This adds per-step evaluation breakdowns showing which fields passed and which failed for every candidate:

```
│ Detailed evaluations:
│   ✗ step 0: no-match
│     ✗ toolName: {"is":"scan_repo"} vs "get_recent_tracks"
│   ✓ step 1: matched
│     ✓ toolName: {"is":"get_recent_tracks"} vs "get_recent_tracks"
```

With compound conditions:

```
│   ✗ step 0: no-match
│     ✓ toolName: {"is":"analyze"} vs "analyze"
│     ✗ params.language: {"is":"python"} vs "typescript"
│   ✓ step 1: matched
│     ✓ toolName: {"is":"analyze"} vs "analyze"
│     ✓ params.language: {"is":"typescript"} vs "typescript"
```

**When to use:** During development or when investigating unexpected behavior. Summary tracing (the default) is sufficient for production.

---

## Trace Levels

Taias captures a trace on every `resolve()` call. The `tracing` option controls how much detail is recorded:

**Summary** (`tracing: "summary"`, the default): Records which step matched, which phase found it, which field indexes were consulted, and how many candidates were evaluated. No additional computation beyond what `resolve()` already performs.

**Detailed** (`tracing: "detailed"`): Additionally records per-step evaluation breakdowns with per-field condition outcomes. Each `StepEvaluation` shows what was expected, what the context contained, and whether each field passed.

---

## Event Emitter (Advanced)

Under the hood, `debug` auto-wires a built-in subscriber to the Taias event emitter. For programmatic access to resolve events, you can use the emitter directly:

```ts
const taias = createTaias({ flow });

taias.on("resolve", (event) => {
  // event: ResolveEvent -- full context, trace, decision, affordances
});

// Unsubscribe:
taias.off("resolve", handler);
```

The `debug` option and manual `on()` subscribers work together -- both fire on each `resolve()` call.

### The ResolveEvent

Every `resolve()` call emits a `ResolveEvent` with four parts:

```ts
{
  flowId: string;              // The flow's id (from defineFlow)
  timestamp: number;           // When resolve was called (Date.now())
  durationMs: number;          // How long resolve took
  context: TaiasContext;       // What was passed to resolve()
  trace: ResolveTrace;         // How the decision was reached
  decision: Decision | null;   // What was decided
  affordances: {               // How the decision was manifested
    advice: string;
    selections: UiSelections;
  } | null;
}
```

Events are emitted even when no step matches (in which case `decision` and `affordances` are `null`).
